import React, { useEffect, useState } from "react";
import "../components/CookieConsent.css";
import i18n from "../i18n";

const COOKIE_KEY = "cookie_consent";
const LANG_KEY = "preferredLang";

function setCookie(name, value, days = 365) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};path=/;expires=${d.toUTCString()};SameSite=Lax`;
}

function getCookie(name) {
  const matches = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return matches ? matches[2] : null;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [consent, setConsent] = useState(() => {
    const c = localStorage.getItem(COOKIE_KEY);
    if (c !== null) return c === "true";
    const ck = getCookie(COOKIE_KEY);
    if (ck !== null) return ck === "true";
    return null;
  });
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || i18n.language || "en");

  useEffect(() => {
    // show banner only when consent not recorded
    if (consent === null) setVisible(true);
    else setVisible(false);
  }, [consent]);

  useEffect(() => {
    // ensure i18n reflects stored preference on mount
    if (lang && i18n.language !== lang) i18n.changeLanguage(lang);
  }, []); // eslint-disable-line

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showDataModal) {
        setShowDataModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showDataModal]);

  function acceptAll() {
    localStorage.setItem(COOKIE_KEY, "true");
    setCookie(COOKIE_KEY, "true", 365);
    setConsent(true);
    setVisible(false);
    setShowDataModal(false);
  }

  function decline() {
    localStorage.setItem(COOKIE_KEY, "false");
    setConsent(false);
    setVisible(false);
    setShowDataModal(false);
  }

  function chooseLang(l) {
    setLang(l);
    localStorage.setItem(LANG_KEY, l);
    i18n.changeLanguage(l);
  }

  const getContent = () => {
    if (lang === 'fr') {
      return {
        title: "Nous utilisons des cookies",
        description: "En continuant, vous acceptez notre utilisation des cookies. Choisissez votre préférence de langue ci-dessous.",
        learnMore: "En savoir plus",
        decline: "Refuser",
        accept: "Accepter",
        modalTitle: "Comment nous utilisons vos données",
        modalSections: {
          essential: {
            title: "🔒 Cookies essentiels",
            content: "Ces cookies sont nécessaires au fonctionnement de notre site web. Ils permettent des fonctionnalités de base comme la navigation dans les pages et l'accès aux zones sécurisées."
          },
          functional: {
            title: "⚙️ Cookies fonctionnels", 
            content: "Ces cookies nous permettent de mémoriser vos préférences (comme la langue) et de personnaliser votre expérience sur notre site."
          },
          analytics: {
            title: "📊 Cookies d'analyse",
            content: "Nous utilisons Google Analytics pour comprendre comment les visiteurs utilisent notre site. Ces données nous aident à améliorer notre contenu et votre expérience."
          },
          dataUse: {
            title: "🛡️ Comment nous protégeons vos données",
            content: "Nous ne vendons jamais vos données personnelles. Toutes les informations collectées sont utilisées uniquement pour améliorer nos services et votre expérience utilisateur. Vos données sont stockées de manière sécurisée et conformément aux lois canadiennes sur la protection de la vie privée."
          },
          retention: {
            title: "⏰ Conservation des données",
            content: "Les cookies de préférences sont conservés pendant 1 an. Les données analytiques sont anonymisées et conservées pendant 26 mois maximum, conformément aux politiques de Google Analytics."
          }
        },
        close: "Fermer"
      };
    }
    return {
      title: "We use cookies",
      description: "By continuing you agree to our use of cookies. Choose your language preference below.",
      learnMore: "Learn More",
      decline: "Decline",
      accept: "Accept",
      modalTitle: "How We Use Your Data",
      modalSections: {
        essential: {
          title: "🔒 Essential Cookies",
          content: "These cookies are necessary for our website to function. They enable basic features like page navigation and access to secure areas."
        },
        functional: {
          title: "⚙️ Functional Cookies",
          content: "These cookies allow us to remember your preferences (like language selection) and personalize your experience on our site."
        },
        analytics: {
          title: "📊 Analytics Cookies", 
          content: "We use Google Analytics to understand how visitors use our site. This data helps us improve our content and your experience."
        },
        dataUse: {
          title: "🛡️ How We Protect Your Data",
          content: "We never sell your personal data. All information collected is used solely to improve our services and your user experience. Your data is stored securely and in compliance with Canadian privacy laws."
        },
        retention: {
          title: "⏰ Data Retention",
          content: "Preference cookies are kept for 1 year. Analytics data is anonymized and retained for a maximum of 26 months, in accordance with Google Analytics policies."
        }
      },
      close: "Close"
    };
  };

  const content = getContent();

  if (!visible) return null;

  return (
    <>
      <div className="cookie-consent" role="dialog" aria-live="polite" aria-label="Cookie preferences">
        <div className="cookie-consent__inner">
          <div className="cookie-consent__text">
            <strong>{content.title}</strong>
            <div className="cookie-consent__desc">
              {content.description}
            </div>
          </div>

          <div className="cookie-consent__controls">
            <div className="cookie-consent__lang">
              <button
                className={`lang-btn ${lang === "en" ? "is-active" : ""}`}
                onClick={() => chooseLang("en")}
                aria-pressed={lang === "en"}
                type="button"
              >
                EN
              </button>
              <button
                className={`lang-btn ${lang === "fr" ? "is-active" : ""}`}
                onClick={() => chooseLang("fr")}
                aria-pressed={lang === "fr"}
                type="button"
              >
                FR
              </button>
            </div>

            <div className="cookie-consent__actions">
              <button 
                className="btn btn-learn-more" 
                onClick={() => setShowDataModal(true)} 
                type="button"
              >
                {content.learnMore}
              </button>
              <button className="btn btn-ghost" onClick={decline} type="button">
                {content.decline}
              </button>
              <button className="btn btn-primary" onClick={acceptAll} type="button">
                {content.accept}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data Usage Modal */}
      {showDataModal && (
        <div 
          className="data-modal-overlay" 
          onClick={() => setShowDataModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="data-modal-title"
        >
          <div 
            className="data-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="data-modal-header">
              <h2 id="data-modal-title">{content.modalTitle}</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowDataModal(false)}
                aria-label="Close modal"
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className="data-modal-body">
              <div className="data-section">
                <h3>{content.modalSections.essential.title}</h3>
                <p>{content.modalSections.essential.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.functional.title}</h3>
                <p>{content.modalSections.functional.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.analytics.title}</h3>
                <p>{content.modalSections.analytics.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.dataUse.title}</h3>
                <p>{content.modalSections.dataUse.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.retention.title}</h3>
                <p>{content.modalSections.retention.content}</p>
              </div>
            </div>

            <div className="data-modal-footer">
              <button className="btn btn-ghost" onClick={decline} type="button">
                {content.decline}
              </button>
              <button className="btn btn-primary" onClick={acceptAll} type="button">
                {content.accept}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}